import { useTranslation } from "react-i18next";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "@uidotdev/usehooks";
import styled from "styled-components";
import { useLocation, useSearchParams } from "react-router-dom";

import { Color } from "../../constants/color";
import { BreakpointsQuery } from "../../constants/device";
import * as Ariakit from "@ariakit/react";
import { CalendarDays, Search, TriangleAlert } from "lucide-react";
import {
  Select,
  SelectItem,
  SelectLabel,
  SelectProvider,
} from "../form/select";
import { useAuth } from "../../provider/authProvider";
import {
  DocumentData,
  RigaPrenotabile,
  RigaSelezionabile,
  SelectedBookableEmployeeType,
  SelezionePrenotazione,
  VociPrenotabili,
  DashboardSummary,
} from "../../types";
import { assignMissingCourse } from "../../features/course/utils";
import { getDashboardSummary } from "../../features/dashboard/utils";
import { RIEPILOGO_VUOTO } from "../../features/dashboard/riepilogo";
import KpiAttestati from "../kpi-attestati";
import {
  useCertificatesDataset,
  usePrenotabili,
} from "../../features/certificate/useCertificatesDataset";
import {
  codiceStatoDaUrl,
  finestraDaUrl,
  filtraPerFinestraScadenza,
  filtraPerSedeECorso,
  filtraPerStato,
  filtraPerTesto,
  nomeCorso,
  opzioniSede,
  statoDaCodice,
} from "../../features/certificate/pipeline";
import {
  chiaveRiga,
  partecipanti,
  selezionabile,
  selezionata,
  senzaRiga,
  toggleSelezione,
} from "../../features/certificate/selezione";
import {
  BadgeStatoAttestato,
  InfoPrenotazione,
  ScaricaAttestato,
} from "./riga-attestato";
import { StatoVuoto } from "./stato-vuoto";
import Suggerimento from "../tooltip/suggerimento";
import { vaiAllaPrenotazione } from "../../features/employee/prenotazione";
import { useAppDispatch } from "../../app/hooks";
import Button from "../button/button";
import { BookableEmployees } from "./BookableEmployees";
import { BarLoader } from "react-spinners";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem 4rem;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: end;
  gap: 2rem;
  @media ${BreakpointsQuery.tablet} {
    justify-content: center;
    gap: 1rem;
  }
`;

const SearchInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  & label {
    margin-left: 0.5rem;
    font-family: Avenir Next, sans-serif;
    font-weight: bold;
    color: ${Color.LIGHT_BLUE};
  }
  & input {
    border: 1px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    padding: 0.2rem 1rem;
    width: 100%;
  }
`;

const SearchButton = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Color.LIGHT_BLUE};
  color: ${Color.WHITE};
  top: 0;
  right: 0;
  bottom: 0;
  width: 2.5rem;
  border-radius: 0 1rem 1rem 0;
`;

const Card = styled.div`
  border-radius: 8px;
  border: 2px solid var(--blue300);
`;

// Voci della select STATO, con il codice numerico del contratto URL
// (`?status=N`, vedi `statoDaCodice` nella pipeline). 0 = nessun filtro.
const statoAttestati: { [key: number]: string } = {
  0: "TUTTI",
  1: "VALIDI",
  2: "IN SCADENZA",
  3: "SCADUTI",
  4: "MANCANTI",
};

type filtersType = {
  corso: string;
  sede: string;
  stato: number;
};

export default function RicercaPerScadenza() {
  const { t } = useTranslation();
  const { token } = useAuth();
  // I parametri `status` ed `expire` arrivano dai KPI (dashboard o blocco in
  // testa a questa tab) tramite la navigazione dell'applicazione: si leggono
  // dal router, così un click su un KPI a tab già montata aggiorna i filtri.
  const [urlParams] = useSearchParams();
  const { key: chiaveNavigazione } = useLocation();

  // Pipeline condivisa: lista nell'ordine canonico del backend con lo stato
  // già calcolato, sedi di lavoro e catalogo, recuperati una volta per visita
  // alla rotta.
  const {
    attestati: tuttiAttestati,
    sediDiLavoro,
    catalogo,
    loading: loadingAttestati,
  } = useCertificatesDataset();

  const sedeAttestati = useMemo(
    () => opzioniSede(sediDiLavoro, t("attestati-sede-tutte")),
    [sediDiLavoro, t]
  );

  const [filters, setFilters] = React.useState<filtersType>({
    corso: "",
    sede: "",
    stato: codiceStatoDaUrl(urlParams),
  });

  const debouncedFilters = useDebounce(filters, 300);
  const [selectedExpire, setSelectedExpire] = useState(() =>
    finestraDaUrl(urlParams)
  );

  // Ogni navigazione (click su un KPI) applica lo stato e la finestra che
  // porta con sé, come faceva il ricaricamento della pagina: anche quando
  // l'indirizzo è identico al precedente e l'utente ha cambiato i filtri a mano.
  useEffect(() => {
    setFilters((prev) => ({ ...prev, stato: codiceStatoDaUrl(urlParams) }));
    setSelectedExpire(finestraDaUrl(urlParams));
  }, [urlParams, chiaveNavigazione]);

  const [kpiData, setKpiData] = useState<DashboardSummary>(RIEPILOGO_VUOTO);

  useEffect(() => {
    // I KPI arrivano in una sola richiesta dal riepilogo del backend.
    getDashboardSummary().then(setKpiData);
  }, [token]);

  // Filtri in pagina sul set condiviso (sede e corso in AND, poi stato),
  // nell'ordine del backend: nessun riordinamento, nessuna richiesta al cambio
  // di filtro. La finestra 1/2/6 mesi restringe solo gli in scadenza.
  const attestati = useMemo(() => {
    const stato = statoDaCodice(debouncedFilters.stato);
    let corsi = filtraPerSedeECorso(tuttiAttestati, debouncedFilters, catalogo);
    corsi = filtraPerStato(corsi, stato);
    if (stato === "in_scadenza") {
      corsi = filtraPerFinestraScadenza(corsi, selectedExpire);
    }
    return corsi;
  }, [tuttiAttestati, catalogo, debouncedFilters, selectedExpire]);

  const selectSede = Ariakit.useSelectStore({
    value: filters.sede,
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, sede: value }));
    },
  });

  const selectStato = Ariakit.useSelectStore({
    value: `${filters.stato}`,
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, stato: +value }));
    },
  });

  return (
    <Layout>
      <SearchContainer>
        <SearchInput>
          <label htmlFor="searchCorso">CORSO</label>
          <div
            style={{
              position: "relative",
              minWidth: "600px",
            }}
          >
            <input
              name="searchCorso"
              type="text"
              placeholder="Ricerca corso"
              aria-label="Ricerca corso"
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, corso: e.target.value }))
              }
            />
            <SearchButton>
              <Search color={Color.WHITE} size={16} width={16} />
            </SearchButton>
          </div>
        </SearchInput>
        <SelectProvider store={selectSede}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.1rem",
            }}
          >
            <SelectLabel
              style={{
                paddingLeft: "0.5rem",
              }}
            >
              SEDE AZIENDALE
            </SelectLabel>
            <Select
              style={{
                minWidth: "200px",
              }}
              renderValue={(value) => sedeAttestati[`${value}`]}
            >
              {Object.entries(sedeAttestati).map(([key, value]) => (
                <SelectItem key={`sedeAttestato_${key}`} value={key}>
                  {value}
                </SelectItem>
              ))}
            </Select>
          </div>
        </SelectProvider>
        <SelectProvider store={selectStato}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.1rem",
            }}
          >
            <SelectLabel
              style={{
                paddingLeft: "0.5rem",
              }}
            >
              STATO
            </SelectLabel>
            <Select
              style={{
                minWidth: "200px",
              }}
              renderValue={(value) => statoAttestati[+value]}
            >
              {Object.entries(statoAttestati).map(([key, value]) => (
                <SelectItem key={`statoAttestato_${key}`} value={key}>
                  {value}
                </SelectItem>
              ))}
            </Select>
          </div>
        </SelectProvider>
      </SearchContainer>

      <Card
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "67rem",
        }}
      >
        <KpiAttestati
          riepilogo={kpiData}
          mesi={selectedExpire}
          onMesiChange={setSelectedExpire}
          larghezzaPiena
        />
      </Card>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2rem",
          width: "78rem",
        }}
      >
        <TableCertificates
          attestati={attestati}
          catalogo={catalogo}
          loadingAttestati={loadingAttestati}
          filtriAttivi={
            !!debouncedFilters.corso ||
            !!debouncedFilters.sede ||
            debouncedFilters.stato !== 0
          }
        />
      </div>
    </Layout>
  );
}

const TableAttestati = styled.div`
  display: grid;
  grid-template-columns: auto 300px 150px 150px 150px 100px 20px;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: bold;
`;

const TableAttestatiHeader = styled(TableAttestati)`
  text-transform: uppercase;
  color: ${Color.LIGHT_BLUE};
`;

const TableAttestatiRow = styled(TableAttestati)`
  border: 1px solid ${Color.GREY};
  border-radius: 16px;
  text-transform: uppercase;
  color: ${Color.DARK_GREY};
  background-color: ${Color.LIGHT_GREY};
  min-height: 3.2rem;
`;

const VOCI_VUOTE: VociPrenotabili = {};

function TableCertificates({
  attestati,
  catalogo,
  loadingAttestati,
  filtriAttivi,
}: {
  attestati: DocumentData[];
  catalogo: Map<string, string>;
  loadingAttestati: boolean;
  filtriAttivi: boolean;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchDiscente, setSearchDiscente] = React.useState("");
  const debouncedSearchDiscente = useDebounce(searchDiscente, 300);
  // Righe spuntate per la prenotazione: da qui si deriva quali checkbox
  // restano abilitati (stesso corso, stesso tipo), senza toccare il DOM.
  const [selezione, setSelezione] = React.useState<SelezionePrenotazione>([]);

  // Le tendine PRENOTA di tutti i corsi arrivano dal batch condiviso, una
  // volta per visita alla rotta e in background, senza bloccare la lista; un
  // cambio di filtro non produce richieste. Assegnazioni e annullamenti fatti
  // da qui correggono l'elenco condiviso.
  const { voci, segnaAssegnato, segnaAnnullato } = usePrenotabili();
  const mapBookableEmployees: VociPrenotabili = voci ?? VOCI_VUOTE;
  const [mapSelectedEmployees, setMapSelectedEmployees] = useState<{
    [key: string]: SelectedBookableEmployeeType[];
  }>({});
  // Corsi mancanti (id del record) la cui assegnazione è stata annullata da
  // questa schermata: il record condiviso non si tocca, la riga si legge come
  // non assegnata.
  const [assegnazioniAnnullate, setAssegnazioniAnnullate] = useState<
    Set<number>
  >(() => new Set());
  const notSubscribedErrorMessage = t("attestati-corso-non-in-abbonamento");

  // Ricerca sul discente in pagina; le righe restano nell'ordine del backend.
  const filterdAttestati = useMemo(
    () => filtraPerTesto(attestati, debouncedSearchDiscente),
    [attestati, debouncedSearchDiscente]
  );

  // Un risultato vuoto si spiega in due modi diversi: o i filtri escludono
  // tutto, o non c'e' ancora nulla da mostrare.
  const filtriConRicerca = filtriAttivi || !!debouncedSearchDiscente;

  function handlePrenotazione() {
    // Seleziono il primo partecipante (in caso ce ne siano più di uno)
    const targetCode = selezione[0].codicePrenotazione;
    // Due righe spuntate della stessa persona la prenotano una volta sola.
    const cfDiscenti = partecipanti(selezione);
    vaiAllaPrenotazione(dispatch, cfDiscenti, targetCode);
  }

  // Le due handler sono stabili: la riga a cui si riferiscono arriva dal
  // figlio, così `BookableMemoized` non si ri-renderizza a ogni render del
  // padre. Leggono i prenotabili dallo stato tramite ref per non dipenderne.
  const mapBookableEmployeesRef = useRef(mapBookableEmployees);
  mapBookableEmployeesRef.current = mapBookableEmployees;

  const handleEmployeeBooked = useCallback(
    async (
      cf: string,
      { corsoMancanteId, codiceCorso, indice }: RigaPrenotabile
    ) => {
      const bookableEmployees =
        mapBookableEmployeesRef.current[codiceCorso] || [];
      const selected = bookableEmployees.find((e) => e.cf === cf);
      if (!selected) {
        return;
      }
      // assegno il dipendente al corso mancante
      const assegnato = await assignMissingCourse(corsoMancanteId, cf);
      if (!assegnato) {
        return;
      }
      // aggiorno la lista dei partecipanti assegnati al corso
      setMapSelectedEmployees((prev) => {
        const s = prev[codiceCorso] || [];
        return {
          ...prev,
          [codiceCorso]: [
            ...s,
            { cf: selected.cf, name: selected.name, index: indice },
          ],
        };
      });
      // rimuovo il dipendente dalla lista dei dipendenti prenotabili
      segnaAssegnato(codiceCorso, selected.cf);
    },
    [segnaAssegnato]
  );

  const handleUndoEmployeeBooked = useCallback(
    async (
      selected: SelectedBookableEmployeeType,
      { corsoMancanteId, codiceCorso }: RigaPrenotabile
    ) => {
      // rimuovo il dipendente dal corso mancante
      const annullato = await assignMissingCourse(corsoMancanteId, null);
      if (!annullato) {
        return;
      }
      if (selected) {
        // rimuovo il dipendente dalla lista dei partecipanti selezionati
        setMapSelectedEmployees((prev) => {
          const s = prev[codiceCorso] || [];
          return {
            ...prev,
            [codiceCorso]: s.filter((se) => se.index !== selected.index),
          };
        });
        // aggiungo il dipendente alla lista dei dipendenti prenotabili, ordinando la lista
        segnaAnnullato(codiceCorso, { cf: selected.cf, name: selected.name });
        // la riga torna a leggersi come corso mancante non assegnato
        setAssegnazioniAnnullate((prev) => new Set(prev).add(corsoMancanteId));
        // e non porta più quel discente: se era spuntata, esce dalla selezione
        setSelezione((prev) =>
          senzaRiga(
            prev,
            chiaveRiga({ id: corsoMancanteId, metadata: { mancante: true } })
          )
        );
      }
    },
    [segnaAnnullato]
  );

  // La lista dipende solo dal caricamento degli attestati; i dipendenti
  // prenotabili (per i corsi mancanti) si caricano in background senza bloccare.
  if (loadingAttestati) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          flexDirection: "column",
          fontSize: "1.5rem",
        }}
      >
        <p>Caricamento corsi...</p>
        <BarLoader width="32ch" loading={loadingAttestati} />
      </div>
    );
  }

  return (
    <>
      <SearchInput style={{ width: "80%" }}>
        <div
          style={{
            position: "relative",
          }}
        >
          <input
            name="searchPartecipantiCorso"
            type="text"
            placeholder="Ricerca i partecipanti"
            aria-label="Ricerca i partecipanti"
            onChange={(e) => setSearchDiscente(e.target.value)}
          />
          <SearchButton>
            <Search color={Color.WHITE} size={16} width={16} />
          </SearchButton>
        </div>
      </SearchInput>
      <div
        style={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          gap: ".5rem",
        }}
      >
        {filterdAttestati && filterdAttestati.length > 0 && (
          <TableAttestatiHeader>
            <p>Cognome e nome</p>
            <p>Corso</p>
            <p>Sede aziendale</p>
            <p>Stato</p>
            <p>Info</p>
            <p
              style={{
                justifySelf: "center",
              }}
            >
              Attestato
            </p>
          </TableAttestatiHeader>
        )}
        {filterdAttestati && filterdAttestati.length === 0 && (
          <StatoVuoto
            icona={CalendarDays}
            titolo={
              filtriConRicerca
                ? t("attestati-tab-scadenza-vuoto-filtri")
                : t("attestati-tab-scadenza-vuoto")
            }
            suggerimento={
              filtriConRicerca
                ? t("attestati-tab-scadenza-vuoto-filtri-suggerimento")
                : t("attestati-tab-scadenza-vuoto-suggerimento")
            }
          />
        )}
        {filterdAttestati &&
          filterdAttestati.length > 0 &&
          filterdAttestati.map((a, index) => {
            const mancante = a.metadata.mancante;
            const cfDiscente = assegnazioniAnnullate.has(a.id)
              ? ""
              : a.metadata.cf_discente;
            const mancanteAssegnato = !!(
              mancante &&
              cfDiscente &&
              !a.metadata.data_prenotazione
            );
            const prenotato = !!(
              mancante &&
              cfDiscente &&
              a.metadata.data_prenotazione
            );
            const assunto = a.isHired;
            let selected = undefined;
            if (mancante) {
              if (mancanteAssegnato || prenotato) {
                selected = {
                  cf: cfDiscente,
                  name: `${a.metadata.cognome_discente} ${a.metadata.nome_discente}`.toUpperCase(),
                  index,
                };
              } else {
                const selectedEmployee =
                  mapSelectedEmployees[a.metadata.codice_corso];
                if (selectedEmployee) {
                  selected = selectedEmployee.find((se) => se.index === index);
                }
              }
            }
            const employeeCF = selected ? selected.cf : cfDiscente;
            const showBookable = (mancante || mancanteAssegnato) && !prenotato;
            const bookableEmployees =
              mapBookableEmployees[a.metadata.codice_corso];
            // La riga nella selezione: il discente della riga (anche se
            // assegnato in questa sessione) e il corso da prenotare, cioè
            // quello proposto dal backend (reservationCode), altrimenti il
            // corso stesso. La chiave distingue due righe della stessa
            // persona sullo stesso corso.
            const chiave = chiaveRiga(a);
            const corso = nomeCorso(a, catalogo);
            const riga: RigaSelezionabile = {
              chiave,
              cf: employeeCF ?? "",
              codiceCorso: a.metadata.codice_corso,
              codicePrenotazione: a.reservationCode
                ? a.reservationCode
                : a.metadata.codice_corso,
              mancante: a.metadata.mancante,
            };
            return (
              <TableAttestatiRow key={`attestato_${a.id}_${index}`}>
                {showBookable ? (
                  <BookableMemoized
                    employees={bookableEmployees}
                    selectedEmployee={selected}
                    corsoMancanteId={a.id}
                    codiceCorso={a.metadata.codice_corso}
                    indice={index}
                    onEmployeeBooked={handleEmployeeBooked}
                    undoEmployeeBooked={handleUndoEmployeeBooked}
                  />
                ) : (
                  <p>
                    {a.metadata.cognome_discente +
                      " " +
                      a.metadata.nome_discente}
                  </p>
                )}
                <p>{corso}</p>
                <p>{a.metadata.sede_azienda}</p>
                <BadgeStatoAttestato attestato={a} />
                <InfoPrenotazione attestato={a} />
                <ScaricaAttestato attestato={a} />
                {!assunto || a.metadata.data_prenotazione !== undefined
                  ? null
                  : employeeCF &&
                    !prenotato &&
                    (a.isSubscribed ? (
                      <Suggerimento
                        testo={
                          selezionabile(selezione, riga)
                            ? null
                            : t("attestati-aiuto-riga-non-combinabile")
                        }
                      >
                        <input
                          id={`attestato_checkbox_${chiave}`}
                          type="checkbox"
                          aria-label={t("attestati-seleziona-riga", {
                            discente:
                              a.metadata.cognome_discente +
                              " " +
                              a.metadata.nome_discente,
                            corso,
                          })}
                          checked={selezionata(selezione, chiave)}
                          disabled={!selezionabile(selezione, riga)}
                          onChange={() =>
                            setSelezione((prev) => toggleSelezione(prev, riga))
                          }
                        />
                      </Suggerimento>
                    ) : (
                      <Ariakit.HovercardProvider>
                        <Ariakit.HovercardAnchor render={<div />}>
                          <TriangleAlert
                            color={Color.RED}
                            height={24}
                            width={24}
                          />
                        </Ariakit.HovercardAnchor>
                        <Ariakit.Hovercard
                          fixed
                          gutter={10}
                          style={{
                            backgroundColor: "var(--red500)",
                            color: "var(--white)",
                            padding: "1em",
                            borderRadius: "8px",
                            width: "28vw",
                          }}
                        >
                          <p>{notSubscribedErrorMessage}</p>
                        </Ariakit.Hovercard>
                      </Ariakit.HovercardProvider>
                    ))}
              </TableAttestatiRow>
            );
          })}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          width: "100%",
        }}
      >
        <Suggerimento testo={t("attestati-aiuto-prenota")}>
          <Button
            label="PRENOTA"
            color={Color.LIGHT_BLUE}
            disabled={selezione.length === 0}
            onClick={handlePrenotazione}
          />
        </Suggerimento>
      </div>
    </>
  );
}

const BookableMemoized = memo(BookableEmployees);
